import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Select,
  SelectChangeEvent,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import {
  MdClose as CloseIcon,
  MdEdit,
  MdDelete,
  MdEvent as CalendarIcon,
  MdWork as WorkIcon,
  MdPerson as PersonIcon,
  MdAttachMoney as MoneyIcon,
  MdSick as SickIcon,
  MdFileDownload as DownloadIcon,
  MdReceipt as ReceiptIcon,
  MdCheck as CheckIcon,
  MdCancel as CancelIcon,
  MdRestore as RestoreIcon
} from 'react-icons/md';
import {
  Screening,
  fetchEmployeeScreening,
  deleteEmployee,
  updateEmployeeStatus,
  listScreenings,
  ListScreeningsPayload
} from '../../../slices/payrolslices/PeopleSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useForm, FormProvider } from 'react-hook-form';
import axiosInstance from 'Services/APIService';

interface EmployeeSummaryModalProps {
  open: boolean;
  onClose: () => void;
  screening: Screening | null;
  onAddDetails: (screening: Screening) => void;
  setDetailsModalOpen: (open: boolean) => void;
}

interface PersonalDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: string;
}

interface EmploymentDetails {
  jobTitle: string;
  startDate: Date | null;
  employmentType: string;
  weeklyWorkHours: string;
  payPeriod: string;
}

interface TaxDetails {
  dateOfBirth: string;
  taxFileNumber: string;
}

interface Payslip {
  id: string;
  payslipEnding: string;
  earning: string;
  tax: string;
  super: string;
  netPay: string;
}

const staticData = {
  leaveRemaining: {
    annualLeave: '120 hrs',
    sickLeave: '80 hrs'
  },
  scheduledLeave: [
    {
      leaveType: 'Annual Leave',
      leavePeriod: '2025-08-10 to 2025-08-15'
    },
    {
      leaveType: 'Sick Leave',
      leavePeriod: '2025-09-01 to 2025-09-02'
    }
  ],
  payslips: [
    {
      id: '1',
      payslipEnding: '2025-08-07',
      earning: '$5,000',
      tax: '$1,200',
      super: '$450',
      netPay: '$3,350'
    },
    {
      id: '2',
      payslipEnding: '2025-07-07',
      earning: '$4,800',
      tax: '$1,150',
      super: '$430',
      netPay: '$3,220'
    }
  ]
};

const EmployeeSummaryModal: React.FC<EmployeeSummaryModalProps> = ({
  open,
  onClose,
  screening,
  onAddDetails,
  setDetailsModalOpen
}) => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(400));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const { loading, error, success, message } = useAppSelector((state) => state.people);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [fullDetails, setFullDetails] = useState<PersonalDetails | null>(null);
  const [employmentDetails, setEmploymentDetails] = useState<EmploymentDetails | null>(null);
  const [taxDetails, setTaxDetails] = useState<TaxDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [expandedPayslip, setExpandedPayslip] = useState<string | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [tempStatus, setTempStatus] = useState<string>('false');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [currentScreeningState, setCurrentScreeningState] = useState<Screening | null>(null);

  const isDataFetched = useRef(false);
  const currentScreeningId = useRef<string | null>(null);
  const currentAvatarPath = useRef<string | null>(null);

  const methods = useForm<PersonalDetails>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: ''
    }
  });

  const { reset } = methods;
  console.log('screening', screening);

  // Mobile-responsive value calculator
  const getResponsiveValue = <T extends any>(desktop: T, mobile: T, smallMobile?: T): T => {
    if (isSmallMobile && smallMobile !== undefined) return smallMobile;
    return isMobile ? mobile : desktop;
  };

  const loginInfo = localStorage.getItem('user-details');
  const userDetails = loginInfo ? JSON.parse(loginInfo) : null;

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin');

  // Check if record is deleted
  const isRecordDeleted = useCallback((screening: Screening | null) => {
    return screening?.isDelete === true || screening?.isDeleted === true;
  }, []);

  // Responsive font sizes
  const fontSizes = {
    h6: getResponsiveValue('1.125rem', '1rem', '0.9375rem'),
    body1: getResponsiveValue('1rem', '0.875rem', '0.8125rem'),
    body2: getResponsiveValue('0.875rem', '0.8125rem', '0.75rem'),
    caption: getResponsiveValue('0.75rem', '0.6875rem', '0.625rem')
  };

  // Responsive spacing
  const spacings = {
    dialogPadding: getResponsiveValue('24px', '16px', '12px'),
    sectionGap: getResponsiveValue('24px', '16px', '12px'),
    itemGap: getResponsiveValue('16px', '12px', '8px'),
    avatarSize: getResponsiveValue('60px', '52px', '44px'),
    iconSize: getResponsiveValue(18, 16, 14)
  };

  console.log('employmentDetails', employmentDetails);

  useEffect(() => {
    if (!open || !screening) {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: ''
      });
      setFullDetails(null);
      setEmploymentDetails(null);
      setTaxDetails(null);
      setAvatarUrl(null);
      setAvatarError(false);
      isDataFetched.current = false;
      currentScreeningId.current = null;
      currentAvatarPath.current = null;
      setExpandedPayslip(null);
      setIsEditingStatus(false);
      setSnackbarOpen(false);
      setCurrentScreeningState(null);
      return;
    }

    const screeningKey = `${screening.employeeId}-${screening.email}`;
    if (currentScreeningId.current === screeningKey && isDataFetched.current) {
      return;
    }

    // Set current screening state
    setCurrentScreeningState(screening);

    // Set temp status based on current state
    if (isRecordDeleted(screening)) {
      setTempStatus('restore');
    } else {
      setTempStatus(screening.status ? 'true' : 'false');
    }

    setAvatarError(false);
    setAvatarUrl(null);
    currentScreeningId.current = screeningKey;
    currentAvatarPath.current = null;
    isDataFetched.current = false;
  }, [open, screening, reset, isRecordDeleted]);

  useEffect(() => {
    if (
      !open ||
      !screening?.employeeId ||
      !screening?.email ||
      isLoading ||
      isDataFetched.current
    ) {
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const action = await dispatch(
          fetchEmployeeScreening({
            group_id: selectedProjectId || '',
            service_id: selectedServiceTitle || 'People_Screening',
            email_key: screening.email,
            id: screening.employeeId,
            isNewRecord: screening.isNewRecord || false
          })
        );

        if (fetchEmployeeScreening.fulfilled.match(action)) {
          const response = action.payload;

          if (response?.details?.personal) {
            const personalData: PersonalDetails = {
              firstName: response.details.personal.firstName || '',
              lastName: response.details.personal.lastName || '',
              email: response.details.personal.email || '',
              phone: response.details.personal.phone || '',
              gender: response.details.personal.gender || ''
            };
            reset(personalData);
            setFullDetails(personalData);
          }

          if (response?.details?.employment) {
            const employmentData: EmploymentDetails = {
              jobTitle: response.details.employment.jobTitle || '',
              startDate: response.details.employment.startDate
                ? new Date(response.details.employment.startDate)
                : null,
              employmentType: response.details.employment.employmentType || '',
              weeklyWorkHours: response.details.employment.weeklyWorkHours || '',
              payPeriod: response.details.employment.payPeriod || ''
            };
            setEmploymentDetails(employmentData);
          }

          if (response?.details?.tax) {
            const taxData: TaxDetails = {
              dateOfBirth: response.details.tax.dateOfBirth || '',
              taxFileNumber: response.details.tax.taxFileNumber || ''
            };
            setTaxDetails(taxData);
          }

          isDataFetched.current = true;
        }
      } catch (error) {
        console.error('Failed to fetch employee screening:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, screening, selectedProjectId, selectedServiceTitle, dispatch, reset, isLoading]);

  useEffect(() => {
    if (!open || !screening?.avatar) {
      return;
    }

    let isCancelled = false;

    const fetchAvatar = async () => {
      try {
        currentAvatarPath.current = screening.avatar;

        const response = await axiosInstance.get(
          `/avatar/get-avatar?path=${encodeURIComponent(screening.avatar)}`,
          {
            responseType: 'blob'
          }
        );

        if (!isCancelled && response.data && response.data.size > 0) {
          const url = URL.createObjectURL(response.data);
          setAvatarUrl(url);
        } else {
          throw new Error('Invalid blob response');
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        if (!isCancelled) {
          setAvatarError(true);
        }
      }
    };

    fetchAvatar();

    return () => {
      isCancelled = true;
    };
  }, [open, screening?.avatar]);

  useEffect(() => {
    return () => {
      if (avatarUrl) {
        URL.revokeObjectURL(avatarUrl);
      }
    };
  }, [avatarUrl]);

  useEffect(() => {
    if (success && message) {
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else if (error) {
      setSnackbarMessage(error);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [success, error, message]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.currentTarget;
      if (!target.src.includes('/default-avatar.png') && !avatarError) {
        setAvatarError(true);
      }
    },
    [avatarError]
  );

  const handleDeleteEmployee = useCallback(() => {
    if (screening && selectedProjectId && selectedServiceTitle) {
      dispatch(
        deleteEmployee({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          email_key: screening.email,
          employee_id: screening.employeeId
        })
      );
      onClose();
    }
  }, [screening, selectedProjectId, selectedServiceTitle, dispatch, onClose]);

  const handleEditScreening = useCallback(() => {
    if (screening) {
      onAddDetails(screening);
      setDetailsModalOpen(true);
    }
    onClose();
  }, [screening, onAddDetails, setDetailsModalOpen, onClose]);

  const togglePayslipExpand = useCallback((id: string) => {
    setExpandedPayslip((prev) => (prev === id ? null : id));
  }, []);

  const handleStatusEditStart = useCallback(() => {
    setIsEditingStatus(true);
  }, []);

  const handleStatusChange = useCallback((event: SelectChangeEvent<string>) => {
    setTempStatus(event.target.value);
  }, []);

  // Updated handleStatusSave to handle restore and regular status changes
  const handleStatusSave = useCallback(() => {
    if (screening && selectedProjectId && selectedServiceTitle) {
      const refreshList = () => {
        const payload: ListScreeningsPayload = {
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || '',
          page_size: 10
        };
        dispatch(listScreenings(payload));
      };

      if (tempStatus === 'restore') {
        // Send restore action
        dispatch(
          updateEmployeeStatus({
            group_id: selectedProjectId,
            service_id: selectedServiceTitle,
            email_key: screening.email,
            employee_id: screening.employeeId,
            status: 'restore'
          } as any)
        ).then((action) => {
          if (updateEmployeeStatus.fulfilled.match(action)) {
            setCurrentScreeningState((prev) => ({
              ...prev!,
              status: true,
              isDelete: false,
              isDeleted: false
            }));
            setTempStatus('true');
            setIsEditingStatus(false);
            refreshList();
          }
        });
      } else {
        // Send boolean status for active/inactive
        const booleanStatus = tempStatus === 'true';
        dispatch(
          updateEmployeeStatus({
            group_id: selectedProjectId,
            service_id: selectedServiceTitle,
            email_key: screening.email,
            employee_id: screening.employeeId,
            status: booleanStatus
          })
        ).then((action) => {
          if (updateEmployeeStatus.fulfilled.match(action)) {
            setCurrentScreeningState((prev) => ({
              ...prev!,
              status: booleanStatus
            }));
            setTempStatus(booleanStatus ? 'true' : 'false');
            setIsEditingStatus(false);
            refreshList();
          }
        });
      }
    }
  }, [screening, selectedProjectId, selectedServiceTitle, dispatch, tempStatus]);

  const handleStatusCancel = useCallback(() => {
    const currentRecord = currentScreeningState || screening;
    if (isRecordDeleted(currentRecord)) {
      setTempStatus('restore');
    } else {
      setTempStatus(currentRecord?.status ? 'true' : 'false');
    }
    setIsEditingStatus(false);
  }, [currentScreeningState, screening, isRecordDeleted]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Get status display for UI
  const getStatusDisplay = useCallback(() => {
    const currentRecord = currentScreeningState || screening;
    if (isRecordDeleted(currentRecord)) {
      return {
        label: 'Removed',
        color: '#9e9e9e',
        backgroundColor: 'rgba(158, 158, 158, 0.1)',
        icon: <RestoreIcon size={14} />
      };
    } else if (currentRecord?.status || tempStatus === 'true') {
      return {
        label: 'Active',
        color: '#2e7d32',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        icon: undefined
      };
    } else {
      return {
        label: 'Inactive',
        color: '#c62828',
        backgroundColor: 'rgba(198, 40, 40, 0.1)',
        icon: undefined
      };
    }
  }, [currentScreeningState, screening, isRecordDeleted, tempStatus]);

  if (!open || !screening) {
    return null;
  }

  const statusDisplay = getStatusDisplay();
  const currentRecord = currentScreeningState || screening;

  console.log('isAdminLogin', isAdminLogin);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            maxWidth: isMobile ? '95%' : '700px',
            margin: isMobile ? 0 : 'auto',
            maxHeight: isMobile ? '80%' : 'calc(100% - 64px)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: spacings.dialogPadding,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: fontSizes.h6 }}>
            Employee Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {/* Only show edit and delete for non-deleted records */}
            {!isRecordDeleted(currentRecord) && (
              <>
                <IconButton
                  size="small"
                  sx={{
                    p: '4px',
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                  onClick={handleEditScreening}
                >
                  <MdEdit size={spacings.iconSize} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    p: '4px',
                    color: 'rgb(211, 47, 47)',
                    '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                  onClick={handleDeleteEmployee}
                >
                  <MdDelete size={spacings.iconSize} />
                </IconButton>
              </>
            )}
            <IconButton
              size="small"
              sx={{
                p: '4px',
                color: 'rgba(0, 0, 0, 0.6)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={onClose}
            >
              <CloseIcon size={spacings.iconSize} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: spacings.dialogPadding }}>
          {isLoading || loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px'
              }}
            >
              <CircularProgress size={getResponsiveValue(32, 28, 24)} />
            </Box>
          ) : (
            <FormProvider {...methods}>
              <Box sx={{ mb: spacings.sectionGap, mt: isMobile ? 0 : '8px' }}>
                {/* Avatar and Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacings.itemGap,
                    mb: spacings.sectionGap
                  }}
                >
                  <Box
                    sx={{
                      width: spacings.avatarSize,
                      height: spacings.avatarSize,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: `calc(${spacings.avatarSize} * 0.4)`,
                      overflow: 'hidden',
                      flexShrink: 0
                    }}
                  >
                    {avatarUrl && !avatarError ? (
                      <img
                        src={avatarUrl}
                        alt={`${
                          fullDetails?.firstName || screening.employeeName || 'Employee'
                        }'s avatar`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={handleImageError}
                      />
                    ) : (
                      getInitials(
                        fullDetails?.firstName || screening.employeeName?.split(' ')[0] || 'U',
                        fullDetails?.lastName || screening.employeeName?.split(' ')[1] || 'U'
                      )
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body1,
                        mb: '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {fullDetails?.firstName || screening.employeeName || 'Employee Name'}{' '}
                      {fullDetails?.lastName || ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {fullDetails?.email || screening.email || ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Job Details Grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isSmallMobile
                      ? '1fr'
                      : isMobile
                      ? '1fr 1fr'
                      : 'repeat(3, 1fr)',
                    gap: spacings.itemGap,
                    p: spacings.itemGap,
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    mb: spacings.itemGap
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        display: 'block',
                        mb: '4px',
                        lineHeight: 1.5
                      }}
                    >
                      JOB TITLE
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {employmentDetails?.jobTitle || 'Not specified'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        display: 'block',
                        mb: '4px',
                        lineHeight: 1.5
                      }}
                    >
                      EMPLOYMENT TYPE
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {employmentDetails?.employmentType || 'Not specified'}
                    </Typography>
                  </Box>

                  {(isMobile && !isSmallMobile) || !isMobile ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: fontSizes.caption,
                          display: 'block',
                          mb: '4px',
                          lineHeight: 1.5
                        }}
                      >
                        STATUS
                      </Typography>
                      {isEditingStatus && isAdminLogin ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Select
                            value={tempStatus}
                            onChange={handleStatusChange}
                            size="small"
                            sx={{
                              height: '24px',
                              fontSize: fontSizes.caption,
                              '& .MuiSelect-select': {
                                py: '4px'
                              }
                            }}
                          >
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                            {screening?.isDelete && (
                              <MenuItem value="restore">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <RestoreIcon size={14} />
                                  Restore
                                </Box>
                              </MenuItem>
                            )}
                          </Select>
                          <IconButton
                            size="small"
                            onClick={handleStatusSave}
                            sx={{ p: '2px', color: '#2e7d32' }}
                            disabled={loading}
                          >
                            <CheckIcon size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleStatusCancel}
                            sx={{ p: '2px', color: '#c62828' }}
                            disabled={loading}
                          >
                            <CancelIcon size={14} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Chip
                            label={statusDisplay.label}
                            size="small"
                            {...(statusDisplay.icon && { icon: statusDisplay.icon })}
                            sx={{
                              backgroundColor: statusDisplay.backgroundColor,
                              color: statusDisplay.color,
                              fontSize: fontSizes.caption,
                              height: '24px',
                              borderRadius: '12px'
                            }}
                          />
                          {isAdminLogin && (
                            <IconButton
                              size="small"
                              onClick={handleStatusEditStart}
                              sx={{ p: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                              <MdEdit size={14} />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : null}
                </Box>

                {/* Basic Details */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isSmallMobile
                      ? '1fr'
                      : isMobile
                      ? '1fr 1fr'
                      : 'repeat(2, 1fr)',
                    gap: spacings.itemGap,
                    p: spacings.itemGap,
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px'
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        lineHeight: 1.5
                      }}
                    >
                      Phone:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {fullDetails?.phone || 'Not provided'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        lineHeight: 1.5
                      }}
                    >
                      Gender:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {fullDetails?.gender || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        lineHeight: 1.5
                      }}
                    >
                      Weekly Hours:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {employmentDetails?.weeklyWorkHours || 'Not set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: fontSizes.caption,
                        lineHeight: 1.5
                      }}
                    >
                      Pay Period:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: fontSizes.body2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {employmentDetails?.payPeriod || 'Not set'}
                    </Typography>
                  </Box>

                  {isSmallMobile && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: fontSizes.caption,
                          lineHeight: 1.5
                        }}
                      >
                        STATUS
                      </Typography>
                      {isEditingStatus && isAdminLogin ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Select
                            value={tempStatus}
                            onChange={handleStatusChange}
                            size="small"
                            sx={{
                              height: '24px',
                              fontSize: fontSizes.caption,
                              '& .MuiSelect-select': {
                                py: '4px'
                              }
                            }}
                          >
                            {screening?.isDelete && (
                              <MenuItem value="restore">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <RestoreIcon size={14} />
                                  Restore
                                </Box>
                              </MenuItem>
                            )}
                          </Select>
                          <IconButton
                            size="small"
                            onClick={handleStatusSave}
                            sx={{ p: '2px', color: '#2e7d32' }}
                            disabled={loading}
                          >
                            <CheckIcon size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleStatusCancel}
                            sx={{ p: '2px', color: '#c62828' }}
                            disabled={loading}
                          >
                            <CancelIcon size={14} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Chip
                            label={statusDisplay.label}
                            size="small"
                            {...(statusDisplay.icon && { icon: statusDisplay.icon })}
                            sx={{
                              backgroundColor: statusDisplay.backgroundColor,
                              color: statusDisplay.color,
                              fontSize: fontSizes.caption,
                              height: '24px',
                              borderRadius: '12px'
                            }}
                          />
                          {isAdminLogin && (
                            <IconButton
                              size="small"
                              onClick={handleStatusEditStart}
                              sx={{ p: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                              <MdEdit size={14} />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {screening.competency && (
                <Box
                  sx={{
                    mt: spacings.itemGap,
                    p: spacings.itemGap,
                    backgroundColor: '#fff3e0',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <WorkIcon size={16} color="#ef6c00" />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500,
                        mb: '4px',
                        fontSize: fontSizes.body2
                      }}
                    >
                      Competency Status
                    </Typography>
                    <Chip
                      label={screening.competency}
                      size="small"
                      sx={{
                        backgroundColor:
                          screening.competency === 'competent'
                            ? 'rgba(46, 125, 50, 0.1)'
                            : 'rgba(198, 40, 40, 0.1)',
                        color: screening.competency === 'competent' ? '#2e7d32' : '#c62828',
                        fontSize: fontSizes.caption,
                        height: '24px',
                        borderRadius: '12px'
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Leave Remaining Section */}
              <Box
                sx={{
                  mt: spacings.sectionGap,
                  p: spacings.itemGap,
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarIcon size={16} color="#2e7d32" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      fontSize: fontSizes.body2,
                      color: '#2e7d32'
                    }}
                  >
                    Leave Remaining
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <Typography variant="body2" sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}>
                    Annual Leave:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                    {staticData.leaveRemaining.annualLeave}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}>
                    Sick Leave:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                    {staticData.leaveRemaining.sickLeave}
                  </Typography>
                </Box>
              </Box>

              {/* Scheduled Leave Section */}
              <Box
                sx={{
                  mt: spacings.itemGap,
                  p: spacings.itemGap,
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SickIcon size={16} color="#0288d1" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      fontSize: fontSizes.body2,
                      color: '#0288d1'
                    }}
                  >
                    Scheduled Leave
                  </Typography>
                </Box>
                {staticData.scheduledLeave.map((leave, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                      p: '8px',
                      backgroundColor: index % 2 === 0 ? 'rgba(2, 136, 209, 0.05)' : 'transparent',
                      borderRadius: '4px'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}>
                      Leave Type:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                      {leave.leaveType}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}>
                      Leave Period:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                      {leave.leavePeriod}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Payslips Section */}
              <Box
                sx={{
                  mt: spacings.itemGap,
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: spacings.itemGap,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <ReceiptIcon size={16} color="#ef6c00" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      fontSize: fontSizes.body2,
                      color: '#ef6c00'
                    }}
                  >
                    Payslips
                  </Typography>
                </Box>
                {staticData.payslips.map((payslip) => (
                  <Box key={payslip.id}>
                    <Box
                      sx={{
                        p: spacings.itemGap,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        backgroundColor: expandedPayslip === payslip.id ? '#fff8e1' : 'white',
                        '&:hover': {
                          backgroundColor: '#fff8e1'
                        }
                      }}
                      onClick={() => togglePayslipExpand(payslip.id)}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {payslip.payslipEnding}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        {payslip.netPay}
                      </Typography>
                    </Box>
                    {expandedPayslip === payslip.id && (
                      <Box
                        sx={{
                          p: spacings.itemGap,
                          backgroundColor: '#fff8e1',
                          borderTop: '1px solid #e0e0e0'
                        }}
                      >
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}
                          >
                            Earning:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                            {payslip.earning}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}
                          >
                            Tax:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                            {payslip.tax}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: fontSizes.body2, fontWeight: 500 }}
                          >
                            Super:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: fontSizes.body2 }}>
                            {payslip.super}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px', mt: '12px' }}>
                          <Chip
                            label="Download"
                            size="small"
                            icon={<DownloadIcon size={14} />}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              color: 'rgba(0, 0, 0, 0.6)',
                              fontSize: fontSizes.caption,
                              height: '24px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.2)' }
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </FormProvider>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmployeeSummaryModal;
